import React, { useState } from 'react';
import { Paper, TextField, Grid, Button, Alert, Box, InputAdornment, IconButton } from '@mui/material';
import { Image as ImageIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { useSitesConfig } from '../SitesContext';
import { prepareFieldUpdates } from '../utils/fieldUtils';
import ImageSelector from '../components/ImageSelector';
import FaviconUploadDialog from '../components/FaviconUploadDialog';

const CompanyTab = () => {
  const { t } = useTranslation();
  const { config, setConfig, handleChange, isFieldConfigurable, isFieldFixed, loadConfig } = useSitesConfig();
  const [saving, setSaving] = useState(false);
  const [imageSelectorOpen, setImageSelectorOpen] = useState(false);
  const [imageSelectorField, setImageSelectorField] = useState(null);
  const [faviconDialogOpen, setFaviconDialogOpen] = useState(false);

  // Campos de company - solo los que son configurables
  const companyFields = [
    { path: 'siteConfig.company.name', label: t('company_name') || 'Nombre de la Empresa' },
    { path: 'siteConfig.company.favicon', label: t('favicon_url') || 'URL del Favicon', helperText: t('favicon_helper') || 'Icono que aparece en la pestaña del navegador' },
    { path: 'siteConfig.company.tagline', label: t('tagline') || 'Tagline', helperText: t('tagline_seo_only') || 'Solo se usa en meta tags y SEO (no se muestra en la web)' },
    { path: 'siteConfig.company.description', label: t('description') || 'Descripción', multiline: true }
  ];
  
  // Filtrar solo los campos configurables
  const fields = companyFields.filter(field => isFieldConfigurable(field.path));

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Preparar updates solo para campos configurables (ya están filtrados)
      const updates = {};
      fields.forEach(field => {
        const value = getFieldValue(field.path);
        updates[field.path] = value;
      });

      if (Object.keys(updates).length === 0) {
        SnackbarUtils.warning(t('no_editable_fields') || 'No hay campos editables en esta sección');
        return;
      }

      const preparedUpdates = prepareFieldUpdates(updates);
      await SitesService.updateFields(preparedUpdates);
      
      // Recargar configuración para obtener valores actualizados
      await loadConfig();
      
      SnackbarUtils.success(t('section_saved_successfully') || 'Sección guardada exitosamente');
    } catch (err) {
      console.error('Error saving company:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      SnackbarUtils.error(errorMessage || (t('error_saving_section') || 'Error al guardar la sección'));
    } finally {
      setSaving(false);
    }
  };

  const getFieldValue = (path) => {
    const value = path.split('.').reduce((obj, key) => obj?.[key], config);
    // Retornar el valor o string vacío si es null/undefined
    return value !== null && value !== undefined ? value : '';
  };

  const handleOpenImageSelector = (fieldPath) => {
    // El favicon siempre se sube y recorta a 192x192; los demás campos eligen de la galería
    if (fieldPath.includes('.favicon')) {
      setFaviconDialogOpen(true);
      return;
    }
    setImageSelectorField(fieldPath);
    setImageSelectorOpen(true);
  };

  const handleImageSelect = (imageUrl) => {
    if (imageSelectorField) {
      handleChange(imageSelectorField, imageUrl);
    }
    setImageSelectorOpen(false);
    setImageSelectorField(null);
  };

  const hasEditableFields = fields.length > 0;

  if (!hasEditableFields) {
    return (
      <Paper sx={{ padding: 3, mt: 2 }}>
        <Alert severity="info">
          {t('no_editable_fields_in_section') || 'No hay campos editables en esta sección para el template actual'}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ padding: 3, mt: 2 }}>
      <Grid container spacing={2}>
        {fields.map((field) => {
          const value = getFieldValue(field.path);
          const isImageField = field.path.includes('.logo') || field.path.includes('Image') || field.path.includes('.favicon');

          return (
            <Grid item xs={12} key={field.path}>
              <TextField
                fullWidth
                label={field.label}
                value={value}
                onChange={(e) => handleChange(field.path, e.target.value)}
                multiline={field.multiline}
                rows={field.multiline ? 4 : 1}
                helperText={field.helperText}
                InputProps={isImageField ? {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => handleOpenImageSelector(field.path)}
                        edge="end"
                        color="primary"
                      >
                        <ImageIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                } : undefined}
              />
              {isImageField && value && (
                <Box sx={{ mt: 1, textAlign: 'center' }}>
                  <img
                    src={value}
                    alt="Preview"
                    style={{
                      maxWidth: '200px',
                      maxHeight: '200px',
                      objectFit: 'contain',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      padding: '8px'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </Box>
              )}
            </Grid>
          );
        })}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
              {saving ? (t('saving') || 'Guardando...') : (t('save') || 'Guardar')}
            </Button>
          </Box>
        </Grid>
      </Grid>
      <FaviconUploadDialog
        open={faviconDialogOpen}
        onClose={() => setFaviconDialogOpen(false)}
        onUploaded={(s3Key) => handleChange('siteConfig.company.favicon', s3Key)}
      />
      <ImageSelector
        open={imageSelectorOpen}
        onClose={() => {
          setImageSelectorOpen(false);
          setImageSelectorField(null);
        }}
        onSelect={handleImageSelect}
        currentValue={imageSelectorField ? getFieldValue(imageSelectorField) : ''}
        label={imageSelectorField?.includes('favicon') ? (t('select_favicon') || 'Seleccionar Favicon') : (t('select_logo') || 'Seleccionar Logo')}
      />
    </Paper>
  );
};

export default CompanyTab;
